import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import { useLocale } from "@/lib/locale";
import { Plus, MessageCircle, Flame } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useLocale();
    const [presences, setPresences] = useState([]);
    const [limits, setLimits] = useState({ max_presences: 1, used_presences: 0, plan_tier: "free" });
    const [loading, setLoading] = useState(true);
    const [todayRec, setTodayRec] = useState([]);
    const [subStatus, setSubStatus] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [pRes, lRes, tRes, sRes] = await Promise.all([
                    api.get("/presences"),
                    api.get("/plan/limits"),
                    api.get("/me/today-recurrences").catch(() => ({ data: { items: [] } })),
                    api.get("/subscriptions/status").catch(() => ({ data: null })),
                ]);
                setPresences(pRes.data);
                setLimits(lRes.data);
                setTodayRec(tRes.data?.items || []);
                setSubStatus(sRes.data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const atLimit = limits.used_presences >= limits.max_presences;

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />
            <main className="max-w-6xl mx-auto px-6 md:px-12 pt-20 pb-28 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-20">
                    <div>
                        <p className="label-eyebrow mb-4">
                            {user?.name ? `${t("dashboard.eyebrow")}, ${user.name.split(" ")[0]}` : t("dashboard.eyebrow")}
                        </p>
                        <h1 className="h-serif text-5xl md:text-6xl leading-[1.1]">
                            {t("dashboard.title_a")} <span className="italic text-ink-gold">{t("dashboard.title_b")}</span>
                        </h1>
                        <p className="text-ink-textMid text-xl mt-6 max-w-xl leading-[1.6]">
                            {t("dashboard.body")}
                        </p>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-3">
                        <div className="text-xs text-ink-textMid tracking-widest uppercase" data-testid="plan-usage">
                            {limits.used_presences}/{limits.max_presences} · {limits.plan_tier}
                        </div>
                        <button
                            onClick={() => nav("/create-presence")}
                            disabled={atLimit}
                            data-testid="dashboard-create-btn"
                            className="btn-primary flex items-center gap-2"
                        >
                            <Plus size={16} /> {t("dashboard.new")}
                        </button>
                        {atLimit && (
                            <p className="text-xs text-ink-danger/80 max-w-[16rem] text-right">
                                {t("dashboard.limit_reached")} <Link to="/upgrade" className="underline" data-testid="dashboard-upgrade-link">{t("dashboard.limit_link")}</Link> {t("dashboard.limit_tail")}
                            </p>
                        )}
                        {user?.is_admin && (
                            <Link
                                to="/admin/analytics"
                                data-testid="dashboard-admin-analytics-link"
                                className="text-[10px] tracking-[0.25em] uppercase text-ink-textLow hover:text-ink-gold transition-colors mt-1"
                            >
                                · Analytics ·
                            </Link>
                        )}
                    </div>
                </div>

                {loading ? (
                    <p className="text-ink-textLow italic font-serif">{t("dashboard.loading")}</p>
                ) : (
                    <>
                        <SubscriptionBanner status={subStatus} />
                        {todayRec.length > 0 && <TodayRecurrencesBanner items={todayRec} />}
                        {presences.length === 0 ? <EmptyState /> : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
                                {presences.map((p) => (
                                    <PresenceCard key={p.presence_id} p={p} />
                                ))}
                            </div>
                        )}
                    </>
                )}

                <div className="mt-28 border-t border-ink-border pt-14">
                    <p className="label-eyebrow mb-4">{t("dashboard.quiet_eyebrow")}</p>
                    <h2 className="h-serif text-3xl md:text-4xl mb-4">{t("dashboard.quiet_title")}</h2>
                    <p className="text-ink-textMid text-xl mb-8 max-w-xl leading-[1.6]">
                        {t("dashboard.quiet_body")}
                    </p>
                    <Link to="/fallback-chat" data-testid="enter-fallback" className="btn-secondary inline-flex items-center gap-2">
                        <MessageCircle size={16} /> {t("dashboard.quiet_cta")}
                    </Link>
                </div>
            </main>
        </div>
    );
}

// ── SubscriptionBanner ────────────────────────────────────────────────
// Three states, all non-pushy:
//   • No sub at all → soft upsell card to pricing ("Vedi i piani")
//   • Active sub → status chip + manage link
//   • Past-due → gentle warning + manage link to fix payment method
function SubscriptionBanner({ status }) {
    const s = status || {};
    const active = s.active;
    const pastDue = s.status === "past_due";
    if (!active && !pastDue) {
        return (
            <div className="mb-12 flex items-center justify-between gap-5 border border-ink-border rounded-sm p-5 md:p-6 bg-ink-surface/40 animate-fade-in" data-testid="sub-banner-none">
                <div className="min-w-0">
                    <p className="label-eyebrow">Custodiscila nel tempo</p>
                    <p className="font-serif text-[1.1rem] text-ink-textHigh mt-1 leading-snug">
                        Un abbonamento ti dà la libertà di tornare, ogni volta che vuoi.
                    </p>
                </div>
                <Link to="/pricing" className="btn-secondary flex-shrink-0" data-testid="sub-banner-see-plans">Vedi i piani</Link>
            </div>
        );
    }
    if (pastDue) {
        return (
            <div className="mb-12 flex items-center justify-between gap-5 border border-ink-gold/50 bg-ink-gold/5 rounded-sm p-5 animate-fade-in" data-testid="sub-banner-pastdue">
                <div className="min-w-0">
                    <p className="label-eyebrow text-ink-gold">Pagamento da risolvere</p>
                    <p className="font-serif text-[1.05rem] text-ink-textHigh mt-1">
                        L'ultimo rinnovo non è andato a buon fine. Puoi aggiornare il metodo di pagamento.
                    </p>
                </div>
                <Link to="/pricing" className="btn-primary flex-shrink-0">Gestisci</Link>
            </div>
        );
    }
    // Active
    return (
        <div className="mb-12 flex items-center justify-between gap-5 border border-ink-gold/40 rounded-sm p-5 bg-ink-gold/5 animate-fade-in" data-testid="sub-banner-active">
            <div className="min-w-0">
                <p className="label-eyebrow text-ink-gold">Piano {planName(s.tier)}</p>
                <p className="text-sm text-ink-textMid mt-1 italic">
                    {s.cancel_at_period_end ? "Termina a fine periodo" : "Rinnovo automatico attivo"}
                </p>
            </div>
            <Link to="/pricing" className="btn-ghost flex-shrink-0">Gestisci</Link>
        </div>
    );
}

function planName(tier) {
    return ({ basic: "Basic", emotional: "Emotional", premium: "Premium" })[tier] || "—";
}

// ── TodayRecurrencesBanner ────────────────────────────────────────────
// Rendered above the presence grid ONLY when the user has a presence whose
// anniversary/birthday/memorial date matches today (month+day). No push,
// no email — a quiet in-app whisper. "Una luce è accesa."
//
// Copy intentionally uses the possessive ("il compleanno di Maria") rather
// than present tense ("Maria compie gli anni") because many presences are
// departed; present-tense phrasing reads cruel for memorial dates.
function TodayRecurrencesBanner({ items }) {
    const { t } = useLocale();
    // Translation key per kind — keeps the phrasing specific and warm.
    const phrase = (item) => {
        const k = item.kind || "anniversary";
        const key = `dashboard.today_${k}`;
        const localized = t(key);
        // Fallback to generic anniversary copy if a new kind is ever added.
        return (localized && !localized.startsWith("dashboard.")) ? localized : t("dashboard.today_anniversary");
    };
    return (
        <div
            className="mb-16 animate-fade-in"
            data-testid="today-recurrences-banner"
            role="region"
            aria-label="today-recurrences"
        >
            {items.map((it) => (
                <Link
                    key={`${it.presence_id}-${it.date}`}
                    to={`/presence/${it.presence_id}/arrives`}
                    state={{ recurrence_context: {
                        kind: it.kind,
                        label: it.label,
                        date: it.date,
                        years_since: it.years_since,
                    }}}
                    data-testid={`today-recurrence-${it.presence_id}`}
                    className="flex items-center gap-6 border border-ink-border bg-ink-surface/60 rounded-sm p-5 md:p-6 mb-3 hover:border-ink-goldLo transition-colors duration-500 group"
                >
                    {/* Candle glyph, not emoji, not icon — matches brand. */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-ink-gold/10 border border-ink-gold/40 flex items-center justify-center">
                        <Flame size={18} className="text-ink-gold animate-pulse" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[0.68rem] tracking-[0.22em] uppercase text-ink-gold">
                            {t("dashboard.today_eyebrow")}
                        </p>
                        <p className="font-serif text-lg md:text-xl text-ink-textHigh mt-1 leading-snug">
                            {phrase(it).replace("{name}", it.name).replace("{label}", it.label || "")}
                        </p>
                    </div>
                    <p className="hidden md:block text-sm text-ink-gold/80 italic flex-shrink-0 group-hover:text-ink-goldHi transition-colors">
                        {t("dashboard.today_cta")}
                    </p>
                </Link>
            ))}
        </div>
    );
}

function EmptyState() {
    const { t } = useLocale();
    return (
        <div className="border border-ink-border rounded-sm p-14 text-center animate-fade-in" data-testid="empty-state">
            <p className="font-serif italic text-3xl text-ink-textHigh mb-6">{t("dashboard.empty_title")}</p>
            <p className="text-ink-textMid text-xl max-w-md mx-auto leading-[1.6]">
                {t("dashboard.empty_body")}
            </p>
            <Link to="/create-presence" className="btn-primary inline-flex items-center gap-2 mt-10">
                <Plus size={16} /> {t("dashboard.empty_cta")}
            </Link>
        </div>
    );
}

function PresenceCard({ p }) {
    const { t } = useLocale();
    return (
        <Link
            to={`/presence/${p.presence_id}`}
            data-testid={`presence-card-${p.presence_id}`}
            className="group block border border-ink-border bg-ink-surface rounded-sm overflow-hidden transition-all duration-500 hover:border-ink-goldLo hover:bg-ink-surfaceUp hover:photo-halo"
        >
            <div className="photo-frame aspect-[4/5] border-0 group-hover:[box-shadow:_inset_0_0_80px_-20px_rgba(203,165,104,0.2)]">
                {p.photo_base64 ? (
                    <img
                        src={`data:${p.photo_mime || "image/jpeg"};base64,${p.photo_base64}`}
                        alt={p.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-[900ms]"
                    />
                ) : (
                    <div className="w-full h-full bg-ink-surfaceUp flex items-center justify-center">
                        <BrandMark monogram size={108} className="opacity-70" />
                    </div>
                )}
            </div>
            <div className="p-7">
                <h3 className="font-serif text-[1.7rem] text-ink-textHigh leading-tight">{p.name}</h3>
                <p className="text-base text-ink-textMid mt-2">{p.relationship}</p>
                {p.has_voice && (
                    <p className="label-eyebrow mt-4 text-ink-gold">{t("dashboard.voice_captured")}</p>
                )}
            </div>
        </Link>
    );
}
